import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PRODUCTS } from "../lib/format";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function NewProperty() {
  const nav = useNavigate();
  const [config, setConfig] = useState({ countries: [], currencies: {} });
  const [form, setForm] = useState({
    property_name: "", property_type: "Villa", product: "CARE", address: "",
    country: "Cameroon", city: "Kumba", region: "South-West", currency: "XAF", tax_rate: "19.25",
    bedrooms: "", bathrooms: "", building_area_m2: "", construction_status: "Completed",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);
  const selectedCountry = config.countries.find((c) => c.name === form.country);
  const onCountry = (name) => {
    const c = config.countries.find((x) => x.name === name);
    setForm({ ...form, country: name, region: c?.regions?.[0] || "",
      currency: c?.currency || form.currency, tax_rate: c ? String(c.tax) : form.tax_rate });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        ...form,
        tax_rate: form.tax_rate ? Number(form.tax_rate) : null,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        building_area_m2: form.building_area_m2 ? Number(form.building_area_m2) : null,
      };
      const { data } = await api.post("/properties", payload);
      toast.success(`Property registered · ${data.code}`);
      nav(`/properties/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl fade-up">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
      </button>
      <p className="overline text-accent">Register My Property</p>
      <h1 className="font-heading text-4xl font-bold tracking-tight mt-1 mb-8">New Property</h1>

      <Card className="p-8 border-border">
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="pname">Property name</Label>
            <Input id="pname" required value={form.property_name} onChange={(e) => set("property_name", e.target.value)} placeholder="Okon Residence" data-testid="property-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Product</Label>
              <Select value={form.product} onValueChange={(v) => set("product", v)}>
                <SelectTrigger data-testid="product-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRODUCTS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label} — {v.desc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger data-testid="type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Villa", "Apartment", "House", "Duplex", "Commercial", "Land"].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="addr">Address</Label>
            <Input id="addr" required value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Fiango, Kumba" data-testid="address-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Country</Label>
              <Select value={form.country} onValueChange={onCountry}>
                <SelectTrigger data-testid="country-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {config.countries.map((c) => <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Region</Label>
              {selectedCountry ? (
                <Select value={form.region} onValueChange={(v) => set("region", v)}>
                  <SelectTrigger data-testid="region-select"><SelectValue placeholder="Region" /></SelectTrigger>
                  <SelectContent>
                    {selectedCountry.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.region} onChange={(e) => set("region", e.target.value)} data-testid="region-input" />
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input id="city" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="city-input" />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(config.currencies || {}).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax">Tax (%)</Label>
              <Input id="tax" type="number" step="0.01" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} data-testid="tax-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bed">Bedrooms</Label>
              <Input id="bed" type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} data-testid="bedrooms-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bath">Bathrooms</Label>
              <Input id="bath" type="number" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} data-testid="bathrooms-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="area">Area (m²)</Label>
              <Input id="area" type="number" value={form.building_area_m2} onChange={(e) => set("building_area_m2", e.target.value)} data-testid="area-input" />
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" disabled={loading} className="rounded-full hover:bg-primary/90" data-testid="submit-property-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Register Property
          </Button>
        </form>
      </Card>
    </div>
  );
}
